import type { Message } from '../../../contracts/harness.types';

/** The system-generated `turn_cancelled` marker the backend appends on a stop. */
export const isCancelMarker = (message: Message | undefined): boolean =>
  message?.event_type === 'agent.turn_cancelled' && !!message.system_generated;

/**
 * A pre-output cancel: the `turn_cancelled` marker at `markerIndex` whose turn
 * produced no assistant output, i.e. the immediately-preceding message is the
 * user prompt. Such a turn was effectively "undone" — the Work chat drops the
 * bubble + marker and returns the prompt to the composer. A post-output cancel
 * has an assistant content message before the marker and is kept (transcript +
 * Stopped note); a thinking-only cancel (`[user, assistant(thinking), marker]`)
 * also counts as output and is kept.
 *
 * This is the SINGLE source of truth for "was this an empty cancel?" — both the
 * timeline suppression and the lifecycle's restore-detection derive from it, so
 * the two can never disagree (which would strand a bubble or lose the prompt).
 */
export const isEmptyPreOutputCancel = (
  messages: Message[],
  markerIndex: number
): boolean =>
  isCancelMarker(messages[markerIndex]) && messages[markerIndex - 1]?.role === 'user';

/**
 * Whether the run's MOST RECENT cancellation was a pre-output (empty) cancel.
 * Scans from the tail for the latest `turn_cancelled` marker rather than
 * requiring it to be the final message — a queued send draining (or another tab
 * starting a turn) after the cancel can append a `[…, marker, userB]` tail, and
 * the restore-detection must still fire for the cancelled turn.
 */
export const isLatestCancelPreOutput = (messages: Message[]): boolean => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (isCancelMarker(messages[index])) return isEmptyPreOutputCancel(messages, index);
  }
  return false;
};
